import React from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { Server, RefreshCw, Shield, CheckCircle } from 'lucide-react';
import Button from '../common/Button';

const ADSyncSection = () => {
  const { t } = useTranslatedContent('adsync');

  return (
    <div id="adsync" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-6">{t('title')}</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Deployment Process */}
        <div className="relative">
          {/* Connection Line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-indigo-300 to-violet-200 transform -translate-y-1/2 hidden lg:block" />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Server,
                title: 'Environment Setup',
                description: 'Initial configuration and system preparation for seamless integration'
              },
              {
                icon: RefreshCw,
                title: 'Directory Sync',
                description: 'Establish secure bidirectional synchronization between systems'
              },
              {
                icon: Shield,
                title: 'Security Configuration',
                description: 'Implement security policies and access controls'
              },
              {
                icon: CheckCircle,
                title: 'Validation & Launch',
                description: 'Final testing and production deployment'
              }
            ].map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative group">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-500 to-violet-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </div>

                  {/* Card */}
                  <div className="pt-8 p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/20 transition-colors duration-300">
                    <div className="flex flex-col items-center text-center">
                      <div className="p-3 bg-gradient-to-br from-blue-500 to-violet-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      
                      <h4 className="text-lg font-semibold text-white mb-2">
                        {step.title}
                      </h4>
                      
                      <p className="text-blue-100">
                        {step.description}
                      </p>

                      {/* Arrow indicator */}
                      {index < 3 && (
                        <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-blue-400 group-hover:scale-125 transition-transform duration-300">
                          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {t('features.items', { returnObjects: true }).map((feature: any, index: number) => (
            <div key={index} className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-violet-500 rounded-lg">
                  <RefreshCw className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.name}</h3>
              <p className="text-blue-100">{feature.description}</p>

              {/* Decorative elements */}
              <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute bottom-4 left-4 w-2 h-2 bg-violet-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <div className="mt-12 text-center">
          <Button
            variant="secondary"
            className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white border-0"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t('hero.buttonText')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ADSyncSection;